import React, { useEffect, useMemo, useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, ReferenceLine,
} from 'recharts';
import PageServices from '../../services/PageServices';
import './VegaTracker.css';

// Momentum Indicator → Vega tab. Per minute (saved by the backend job in vega_tracker):
// total Vega of the Call / Put strikes with |Delta| 0.05–0.60, shown as the change since 09:16;
// Difference = Put change − Call change (positive → Bearish, negative → Bullish).

const OPTIONS = [
  { value: 'NIFTY', label: 'Nifty 50' },
  { value: 'BANKNIFTY', label: 'Bank Nifty' },
];
const REFRESH_MS = 60 * 1000;
const istToday = () => new Date(Date.now() + 330 * 60 * 1000).toISOString().slice(0, 10);
const fmt = (v) => (Number.isFinite(Number(v)) ? Number(v).toFixed(2) : '—');

const VegaTracker = () => {
  const [symbol, setSymbol] = useState('NIFTY');
  const [date, setDate] = useState(istToday());
  const [show, setShow] = useState({ call: true, put: true });
  const [result, setResult] = useState(null);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    let cancelled = false;
    const load = (first) => {
      if (first) setStatus('loading');
      PageServices.getVegaTracker(symbol, date)
        .then((res) => {
          if (cancelled) return;
          setResult(res?.data || null);
          setStatus(res?.data?.points?.length ? 'ready' : 'empty');
        })
        .catch(() => { if (!cancelled && first) setStatus('error'); });
    };
    load(true);
    const id = date === istToday() ? setInterval(() => load(false), REFRESH_MS) : null; // today keeps updating
    return () => { cancelled = true; if (id) clearInterval(id); };
  }, [symbol, date]);

  const points = useMemo(() => result?.points || [], [result]);
  const latest = points[points.length - 1];
  const rows = useMemo(() => [...points].reverse(), [points]);

  return (
    <div className="vt-page">
      <div className="vt-toolbar">
        <label className="vt-field">
          <span>Select Option Chain</span>
          <select value={symbol} onChange={(e) => setSymbol(e.target.value)}>
            {OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>
        <label className="vt-field">
          <span>Date</span>
          <input type="date" value={date} max={istToday()} onChange={(e) => setDate(e.target.value)} />
        </label>
      </div>

      <div className="vt-layout">
        <section className="vt-main">
          <h2 className="vt-expiry">Expiry : {result?.expiry || '—'}</h2>
          <div className="vt-summary">
            <div className="vt-sum vt-sum-call"><div>Call Vega</div><strong>{fmt(latest?.callChange)}</strong></div>
            <div className="vt-sum vt-sum-put"><div>Put Vega</div><strong>{fmt(latest?.putChange)}</strong></div>
            <div className="vt-sum vt-sum-diff"><div>Difference</div><strong>{fmt(latest?.difference)}</strong></div>
          </div>

          <div className="vt-toggles">
            <button type="button" className={`vt-tog vt-tog-call${show.call ? ' on' : ''}`} onClick={() => setShow((s) => ({ ...s, call: !s.call }))}>Call Vega</button>
            <button type="button" className={`vt-tog vt-tog-put${show.put ? ' on' : ''}`} onClick={() => setShow((s) => ({ ...s, put: !s.put }))}>Put Vega</button>
            <small>Change since 09:16 · strikes with |Δ| {result?.deltaRange?.join('–') || '0.05–0.6'}</small>
          </div>

          <div className="vt-chart">
            {status === 'ready' ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={points} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" className="vt-grid" />
                  <XAxis dataKey="time" minTickGap={30} />
                  <YAxis />
                  <ReferenceLine y={0} stroke="#94a3b8" />
                  <Tooltip formatter={(v, n) => [fmt(v), n]} />
                  {show.call && <Line type="monotone" dataKey="callChange" name="Call Vega" stroke="#2e7d32" dot={false} strokeWidth={2} isAnimationActive={false} />}
                  {show.put && <Line type="monotone" dataKey="putChange" name="Put Vega" stroke="#e53935" dot={false} strokeWidth={2} isAnimationActive={false} />}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <p className="vt-msg">
                {status === 'loading' ? 'Loading…' : status === 'error' ? 'Could not load Vega data.' : 'No Vega data for this date.'}
              </p>
            )}
          </div>
        </section>

        <aside className="vt-table-wrap">
          <table className="vt-table">
            <thead>
              <tr><th>Time</th><th>Call Vega</th><th>Put Vega</th><th>Difference</th><th>Trend</th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.minute}>
                  <td className="vt-time">{r.time}</td>
                  <td>{fmt(r.callChange)}</td>
                  <td>{fmt(r.putChange)}</td>
                  <td>{fmt(r.difference)}</td>
                  <td><span className={`vt-trend vt-${r.trend.toLowerCase()}`}>{r.trend}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </aside>
      </div>
    </div>
  );
};

export default VegaTracker;
